import React, { useContext, useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { GlobalContext } from '../../../context/GlobalContext';
import useApi from '../../../hooks/useApi';
import { Loader2, Calendar, FileText } from 'lucide-react';
import { format } from 'date-fns';
import ExportDropdown from '../shared/ExportDropdown';

// Report Components from Advanced Reports
import InvoiceSummary from './InvoiceSummary';
import SixMonthSales from './SixMonthSales';
import AreaAnalysis from './AreaAnalysis';
import DailySales from './DailySales';
import WeeklySales from './WeeklySales';
import QuotesReport from './QuotesReport';

function SalesAnalysis() {
  const { operatorData, apir } = useContext(GlobalContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentWeek = getISOWeek();

  // Get report type from URL or default to invoice-summary
  const reportTypeFromUrl = searchParams.get('type') || 'invoice-summary';

  const [filters, setFilters] = useState({
    reportType: reportTypeFromUrl,
    month: currentMonth,
    year: currentYear,
    week: currentWeek,
    rep: '',
    branch: '',
    searchTerm: '',
    period: 'today',
    startDate: format(new Date(), 'yyyyMMdd'),
    endDate: format(new Date(), 'yyyyMMdd')
  });

  // Helper function to get ISO week number
  function getISOWeek() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
    const week1 = new Date(date.getFullYear(), 0, 4);
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
  }

  const { data: branches } = useApi({
    url: '/factor/cplusassist/branches',
    initial: [],
    enabled: !!apir
  });

  const { data: operators } = useApi({
    url: '/salesassist/cplusassist/getOperators',
    initial: [],
    enabled: !!apir && filters.reportType === 'weekly-sales'
  });

  const {
    data: salesData,
    isLoading,
    isFetching,
    refetch: fetchSalesData
  } = useApi({
    url: filters.reportType === 'invoice-summary'
      ? `/visiondashboard/salesassist/getSales/${filters.year}/${filters.month}/${filters.branch}`
      : filters.reportType === 'six-month-sales'
        ? `/visiondashboard/salesassist/getSixMonthSales/${filters.branch}`
        : filters.reportType === 'area-analysis'
          ? `/visiondashboard/salesassist/getAreaAnalysis/${filters.period}/${filters.branch}`
          : filters.reportType === 'weekly-sales'
            ? `/visiondashboard/salesassist/getWeeklySales/${filters.week}${filters.rep && filters.rep !== '' ? `/${filters.rep}` : ''}`
            : `/visiondashboard/salesassist/getDailySales/${filters.startDate.replace(/-/g, '')}/${filters.endDate.replace(/-/g, '')}/${filters.branch || ''}`,
    initial: [],
    enabled: false
  });

  // Update URL when report type changes
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('type', filters.reportType);
    setSearchParams(newParams, { replace: true });
  }, [filters.reportType]);

  // Sync report type from URL
  useEffect(() => {
    const urlType = searchParams.get('type');
    if (urlType && urlType !== filters.reportType) {
      setFilters(prev => ({ ...prev, reportType: urlType }));
    }
  }, [searchParams]);

  const handleFetch = () => {
    fetchSalesData();
  };

  const renderFilters = () => {
    switch (filters.reportType) {
      case 'invoice-summary':
        return (
          <>
            <select
              value={filters.month}
              onChange={(e) => setFilters(prev => ({ ...prev, month: parseInt(e.target.value) }))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              disabled={isFetching}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                <option key={month} value={month}>
                  {new Date(2024, month - 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>

            <select
              value={filters.year}
              onChange={(e) => setFilters(prev => ({ ...prev, year: parseInt(e.target.value) }))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              disabled={isFetching}
            >
              {Array.from({ length: 4 }, (_, i) => currentYear - i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </>
        );

      case 'weekly-sales':
        return (
          <>
            <select
              value={filters.week}
              onChange={(e) => setFilters(prev => ({ ...prev, week: parseInt(e.target.value) }))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              disabled={isFetching}
            >
              {Array.from({ length: 52 }, (_, i) => i + 1).map(week => (
                <option key={week} value={week}>Week {week}</option>
              ))}
            </select>
            <select
              value={filters.branch}
              onChange={(e) => setFilters(prev => ({ ...prev, branch: e.target.value, rep: '' }))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              disabled={isFetching}
            >
              <option value="">All Branches</option>
              {branches && branches.map((branch) => (
                <option key={branch._id} value={branch.MamCode}>
                  {branch.MamCode}
                </option>
              ))}
            </select>
            <select
              value={filters.rep}
              onChange={(e) => setFilters(prev => ({ ...prev, rep: e.target.value }))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              disabled={isFetching}
            >
              <option value="">All Operators</option>
              {operators && operators
                .filter(op => !filters.branch || op.Branch.toLowerCase() === filters.branch.toLowerCase())
                .map((operator) => (
                  <option key={operator.Inits} value={operator.Inits}>
                    {`${operator.Inits} - ${operator.Name}`}
                  </option>
                ))}
            </select>
          </>
        );

      case 'area-analysis':
        return (
          <select
            value={filters.period}
            onChange={(e) => setFilters(prev => ({ ...prev, period: e.target.value }))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            disabled={isFetching}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        );

      case 'daily-sales':
        return (
          <div className="flex gap-2 items-center">
            <div className="relative">
              <input
                type="date"
                value={`${filters.startDate.slice(0, 4)}-${filters.startDate.slice(4, 6)}-${filters.startDate.slice(6, 8)}`}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const formattedDate = format(selectedDate, 'yyyyMMdd');
                  setFilters(prev => ({
                    ...prev,
                    startDate: formattedDate
                  }));
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pl-10"
                disabled={isFetching}
              />
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
            <span className="text-gray-500 text-sm">to</span>
            <div className="relative">
              <input
                type="date"
                value={`${filters.endDate.slice(0, 4)}-${filters.endDate.slice(4, 6)}-${filters.endDate.slice(6, 8)}`}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const formattedDate = format(selectedDate, 'yyyyMMdd');
                  setFilters(prev => ({
                    ...prev,
                    endDate: formattedDate
                  }));
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pl-10"
                disabled={isFetching}
              />
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Refs to access export handlers from child components
  const reportRef = useRef(null);

  // Export handlers that delegate to child components
  const handleExportPDF = () => {
    if (reportRef.current && reportRef.current.handleExportPDF) {
      reportRef.current.handleExportPDF();
    }
  };

  const handleExportExcel = () => {
    if (reportRef.current && reportRef.current.handleExportExcel) {
      reportRef.current.handleExportExcel();
    }
  };

  const handleEmailReport = () => {
    if (reportRef.current && reportRef.current.handleEmailReport) {
      reportRef.current.handleEmailReport();
    }
  };

  const getReportTitle = () => {
    switch (filters.reportType) {
      case 'invoice-summary':
        return 'Invoice Summary';
      case 'six-month-sales':
        return '6 Month Sales';
      case 'area-analysis':
        return 'Area Analysis';
      case 'weekly-sales':
        return 'Weekly Sales';
      case 'daily-sales':
        return 'Daily Sales';
      case 'quotes-report':
        return 'Quotes Report';
      default:
        return 'Sales Analysis';
    }
  };

  const getReportSubtitle = () => {
    switch (filters.reportType) {
      case 'invoice-summary':
        return 'Daily sales breakdown by customer/rep';
      case 'six-month-sales':
        return 'Sales by area and customer over 6 months';
      case 'area-analysis':
        return 'Sales analysis by area';
      case 'weekly-sales':
        return 'Weekly sales breakdown by customer';
      case 'daily-sales':
        return 'Daily sales breakdown';
      case 'quotes-report':
        return 'View and analyze outstanding quotes across all branches and operators';
      default:
        return '';
    }
  };

  const renderReport = () => {
    switch (filters.reportType) {
      case 'invoice-summary':
        return <InvoiceSummary ref={reportRef} data={salesData} isLoading={isLoading} searchTerm={filters.searchTerm} filters={filters} hideHeader={true} />;
      case 'six-month-sales':
        return <SixMonthSales ref={reportRef} data={salesData} isLoading={isLoading} searchTerm={filters.searchTerm} hideHeader={true} />;
      case 'area-analysis':
        return <AreaAnalysis ref={reportRef} data={salesData} isLoading={isLoading} searchTerm={filters.searchTerm} hideHeader={true} />;
      case 'weekly-sales':
        return <WeeklySales ref={reportRef} data={salesData} isLoading={isLoading} searchTerm={filters.searchTerm} hideHeader={true} />;
      case 'daily-sales':
        return <DailySales ref={reportRef} data={salesData} isLoading={isLoading} hideHeader={true} />;
      case 'quotes-report':
        // QuotesReport has its own ReportLayout with header and exports, so render it standalone
        return <QuotesReport />;
      case 'quotes-report':
        // Quotes report doesn't need filters in this section (has its own)
        return null;
      default:
        return null;
    }
  };

  // If quotes-report, render it standalone (it has its own ReportLayout)
  if (filters.reportType === 'quotes-report') {
    return <QuotesReport />;
  }

  return (
    <div className="min-h-screen">
      {isFetching && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center gap-3">
            <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
            <span className="text-gray-700 font-medium">Loading report data...</span>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gray-50 pt-20">
        {/* Report Header with Export Dropdown */}
        <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{getReportTitle()}</h1>
                {getReportSubtitle() && (
                  <p className="text-sm text-gray-600 mt-1">{getReportSubtitle()}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <ExportDropdown
                onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel}
                onEmailReport={handleEmailReport}
                loading={isLoading || isFetching}
                disabled={!salesData || salesData.length === 0}
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-6">
          {/* KPI Cards - First */}
          {/* Invoice Summary KPI Cards */}
          {salesData && salesData.length > 0 && filters.reportType === 'invoice-summary' && (
            <div className="mb-6">
              {(() => {
                const overallTotals = salesData.reduce((acc, item) => ({
                  sales: acc.sales + (item.sales || 0),
                  cost: acc.cost + (item.cost || 0),
                  marginValue: acc.marginValue + (item.marginValue || 0),
                  invoices: acc.invoices + (item.invoices || 0),
                  credits: acc.credits + (item.credits || 0)
                }), { sales: 0, cost: 0, marginValue: 0, invoices: 0, credits: 0 });

                const overallMarginPercent = overallTotals.sales > 0 
                  ? (overallTotals.marginValue / overallTotals.sales * 100) 
                  : 0;

                return (
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                      <div className="text-sm font-medium text-gray-600 mb-1">Total Sales</div>
                      <div className="text-2xl font-bold text-gray-900">£{overallTotals.sales.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
                      <div className="text-xs text-gray-500 mt-1">All Records</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-orange-500">
                      <div className="text-sm font-medium text-gray-600 mb-1">Total Cost</div>
                      <div className="text-2xl font-bold text-gray-900">£{overallTotals.cost.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
                      <div className="text-xs text-gray-500 mt-1">All Records</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
                      <div className="text-sm font-medium text-gray-600 mb-1">Total Margin</div>
                      <div className="text-2xl font-bold text-gray-900">£{overallTotals.marginValue.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {overallMarginPercent.toFixed(1)}% margin
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
                      <div className="text-sm font-medium text-gray-600 mb-1">Total Invoices</div>
                      <div className="text-2xl font-bold text-gray-900">£{overallTotals.invoices.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
                      <div className="text-xs text-gray-500 mt-1">Sales before credits</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-red-500">
                      <div className="text-sm font-medium text-gray-600 mb-1">Total Credits</div>
                      <div className="text-2xl font-bold text-gray-900">£{overallTotals.credits.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
                      <div className="text-xs text-gray-500 mt-1">All Records</div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Weekly Sales KPI Cards */}
          {salesData && salesData.data && Array.isArray(salesData.data) && filters.reportType === 'weekly-sales' && (() => {
            const filteredData = salesData.data.filter(item => {
              if (!filters.searchTerm) return true;
              const search = filters.searchTerm.toLowerCase();
              return item?.name?.toLowerCase().includes(search) || item?.area?.toLowerCase().includes(search);
            });
            const total = filteredData.reduce((sum, item) => sum + (item?.totalValue || 0), 0);
            const avgPerCustomer = filteredData.length > 0 ? total / filteredData.length : 0;
            
            return (
              <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                    <div className="text-sm font-medium text-gray-600 mb-1">Total Sales</div>
                    <div className="text-2xl font-bold text-gray-900">£{Math.round(total).toLocaleString('en-GB')}</div>
                    <div className="text-xs text-gray-500 mt-1">All Customers</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
                    <div className="text-sm font-medium text-gray-600 mb-1">Customer Count</div>
                    <div className="text-2xl font-bold text-gray-900">{filteredData.length}</div>
                    <div className="text-xs text-gray-500 mt-1">Customers</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
                    <div className="text-sm font-medium text-gray-600 mb-1">Avg per Customer</div>
                    <div className="text-2xl font-bold text-gray-900">£{Math.round(avgPerCustomer).toLocaleString('en-GB')}</div>
                    <div className="text-xs text-gray-500 mt-1">Average</div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Daily Sales KPI Cards */}
          {salesData && salesData.totals && filters.reportType === 'daily-sales' && (() => {
            const totals = salesData.totals;
            return (
              <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                    <div className="text-sm font-medium text-gray-600 mb-1">Total Sales</div>
                    <div className="text-2xl font-bold text-gray-900">£{Math.round(totals.total || 0).toLocaleString('en-GB')}</div>
                    <div className="text-xs text-gray-500 mt-1">With VAT</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
                    <div className="text-sm font-medium text-gray-600 mb-1">Total Transactions</div>
                    <div className="text-2xl font-bold text-gray-900">{(totals.totalCount || 0).toLocaleString('en-GB')}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {(totals.invoiceCount || 0).toLocaleString('en-GB')} invoices • {(totals.creditCount || 0).toLocaleString('en-GB')} credits
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
                    <div className="text-sm font-medium text-gray-600 mb-1">Invoice Value</div>
                    <div className="text-2xl font-bold text-gray-900">£{Math.round(totals.invoiceValue || 0).toLocaleString('en-GB')}</div>
                    <div className="text-xs text-gray-500 mt-1">Total invoiced</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-red-500">
                    <div className="text-sm font-medium text-gray-600 mb-1">Credit Value</div>
                    <div className="text-2xl font-bold text-gray-900">£{Math.round(Math.abs(totals.creditValue || 0)).toLocaleString('en-GB')}</div>
                    <div className="text-xs text-gray-500 mt-1">Total credited</div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Six Month Sales KPI Cards */}
          {salesData && salesData.data && Array.isArray(salesData.data) && filters.reportType === 'six-month-sales' && (() => {
            const areasMap = new Map();
            salesData.data.forEach(customer => {
              const area = customer.area || 'Unassigned';
              if (!areasMap.has(area)) {
                areasMap.set(area, { total: 0, customers: new Set() });
              }
              const areaData = areasMap.get(area);
              areaData.total += customer.total || 0;
              areaData.customers.add(customer.customerCode || customer.customerName);
            });
            
            const totalSales = Array.from(areasMap.values()).reduce((sum, area) => sum + area.total, 0);
            const totalCustomers = Array.from(areasMap.values()).reduce((sum, area) => sum + area.customers.size, 0);
            const areaCount = areasMap.size;
            const avgPerArea = areaCount > 0 ? totalSales / areaCount : 0;
            
            return (
              <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                    <div className="text-sm font-medium text-gray-600 mb-1">Total Sales</div>
                    <div className="text-2xl font-bold text-gray-900">£{Math.round(totalSales).toLocaleString('en-GB')}</div>
                    <div className="text-xs text-gray-500 mt-1">All Areas</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
                    <div className="text-sm font-medium text-gray-600 mb-1">Total Areas</div>
                    <div className="text-2xl font-bold text-gray-900">{areaCount}</div>
                    <div className="text-xs text-gray-500 mt-1">Areas</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
                    <div className="text-sm font-medium text-gray-600 mb-1">Total Customers</div>
                    <div className="text-2xl font-bold text-gray-900">{totalCustomers.toLocaleString('en-GB')}</div>
                    <div className="text-xs text-gray-500 mt-1">Customers</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-orange-500">
                    <div className="text-sm font-medium text-gray-600 mb-1">Avg per Area</div>
                    <div className="text-2xl font-bold text-gray-900">£{Math.round(avgPerArea).toLocaleString('en-GB')}</div>
                    <div className="text-xs text-gray-500 mt-1">Average</div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Six Month Sales Top 3 Areas Cards */}
          {salesData && salesData.data && Array.isArray(salesData.data) && filters.reportType === 'six-month-sales' && (() => {
            const areasMap = new Map();
            salesData.data.forEach(customer => {
              const area = customer.area || 'Unassigned';
              if (!areasMap.has(area)) {
                areasMap.set(area, { total: 0, customers: new Set() });
              }
              const areaData = areasMap.get(area);
              areaData.total += customer.total || 0;
              areaData.customers.add(customer.customerCode || customer.customerName);
            });
            
            const topAreas = Array.from(areasMap.entries())
              .map(([area, data]) => ({
                area,
                total: data.total,
                customerCount: data.customers.size
              }))
              .sort((a, b) => b.total - a.total)
              .slice(0, 3);
            
            if (topAreas.length === 0) return null;
            
            return (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 3 Areas by Sales</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {topAreas.map((areaData, index) => {
                    const colors = [
                      { bg: 'from-yellow-50 to-yellow-100', border: 'border-yellow-200', text: 'text-yellow-700', textDark: 'text-yellow-900', textLight: 'text-yellow-600', badge: 'bg-yellow-500' },
                      { bg: 'from-gray-50 to-gray-100', border: 'border-gray-200', text: 'text-gray-700', textDark: 'text-gray-900', textLight: 'text-gray-600', badge: 'bg-gray-500' },
                      { bg: 'from-amber-50 to-amber-100', border: 'border-amber-200', text: 'text-amber-700', textDark: 'text-amber-900', textLight: 'text-amber-600', badge: 'bg-amber-500' }
                    ];
                    const color = colors[index] || colors[0];
                    
                    return (
                      <div key={areaData.area} className={`bg-gradient-to-br ${color.bg} rounded-lg p-4 border ${color.border} hover:shadow-md transition-shadow`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className={`text-xs font-medium ${color.text} px-2 py-1 rounded ${color.badge} text-white`}>
                            #{index + 1}
                          </div>
                          <div className={`text-xs ${color.textLight}`}>
                            {areaData.customerCount} {areaData.customerCount === 1 ? 'customer' : 'customers'}
                          </div>
                        </div>
                        <div className={`text-sm font-semibold ${color.textDark} mb-1`}>{areaData.area}</div>
                        <div className={`text-2xl font-bold ${color.textDark}`}>£{Math.round(areaData.total).toLocaleString('en-GB')}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Area Analysis KPI Cards */}
          {salesData && salesData.data && Array.isArray(salesData.data) && filters.reportType === 'area-analysis' && (() => {
            if (!salesData.data || salesData.data.length === 0) return null;
            
            const overallTotals = salesData.data.reduce((acc, area) => ({
              totalSales: acc.totalSales + (area.currentPeriod?.sales || 0),
              totalMargin: acc.totalMargin + (area.currentPeriod?.marginValue || 0),
              totalCustomers: acc.totalCustomers + (area.totalCustomers || 0),
              activeCustomers: acc.activeCustomers + (area.currentPeriod?.activeCustomers || 0),
              areaCount: acc.areaCount + 1
            }), { totalSales: 0, totalMargin: 0, totalCustomers: 0, activeCustomers: 0, areaCount: 0 });
            
            const marginPercent = overallTotals.totalSales > 0 ? (overallTotals.totalMargin / overallTotals.totalSales * 100) : 0;
            const activePercent = overallTotals.totalCustomers > 0 ? (overallTotals.activeCustomers / overallTotals.totalCustomers * 100) : 0;
            
            return (
              <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                    <div className="text-sm font-medium text-gray-600 mb-1">Total Sales</div>
                    <div className="text-2xl font-bold text-gray-900">£{Math.round(overallTotals.totalSales).toLocaleString('en-GB')}</div>
                    <div className="text-xs text-gray-500 mt-1">All Areas</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
                    <div className="text-sm font-medium text-gray-600 mb-1">Total Margin</div>
                    <div className="text-2xl font-bold text-gray-900">£{Math.round(overallTotals.totalMargin).toLocaleString('en-GB')}</div>
                    <div className="text-xs text-gray-500 mt-1">{marginPercent.toFixed(1)}% margin</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
                    <div className="text-sm font-medium text-gray-600 mb-1">Total Areas</div>
                    <div className="text-2xl font-bold text-gray-900">{overallTotals.areaCount}</div>
                    <div className="text-xs text-gray-500 mt-1">Areas</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-orange-500">
                    <div className="text-sm font-medium text-gray-600 mb-1">Total Customers</div>
                    <div className="text-2xl font-bold text-gray-900">{overallTotals.totalCustomers.toLocaleString('en-GB')}</div>
                    <div className="text-xs text-gray-500 mt-1">All customers</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-red-500">
                    <div className="text-sm font-medium text-gray-600 mb-1">Active Customers</div>
                    <div className="text-2xl font-bold text-gray-900">{overallTotals.activeCustomers.toLocaleString('en-GB')}</div>
                    <div className="text-xs text-gray-500 mt-1">{activePercent.toFixed(1)}% active</div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Area Analysis Top 6 Areas Cards */}
          {salesData && salesData.data && Array.isArray(salesData.data) && filters.reportType === 'area-analysis' && (() => {
            if (!salesData.data || salesData.data.length === 0) return null;
            
            const topAreas = salesData.data
              .map(area => ({
                area: area.area || 'Unknown',
                sales: area.currentPeriod?.sales || 0,
                customers: area.totalCustomers || 0
              }))
              .sort((a, b) => b.sales - a.sales)
              .slice(0, 6);
            
            if (topAreas.length === 0) return null;
            
            return (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 6 Areas by Sales</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {topAreas.map((areaData, index) => {
                    const colors = [
                      { bg: 'from-yellow-50 to-yellow-100', border: 'border-yellow-200', text: 'text-yellow-700', textDark: 'text-yellow-900', textLight: 'text-yellow-600', badge: 'bg-yellow-500' },
                      { bg: 'from-gray-50 to-gray-100', border: 'border-gray-200', text: 'text-gray-700', textDark: 'text-gray-900', textLight: 'text-gray-600', badge: 'bg-gray-500' },
                      { bg: 'from-amber-50 to-amber-100', border: 'border-amber-200', text: 'text-amber-700', textDark: 'text-amber-900', textLight: 'text-amber-600', badge: 'bg-amber-500' },
                      { bg: 'from-blue-50 to-blue-100', border: 'border-blue-200', text: 'text-blue-700', textDark: 'text-blue-900', textLight: 'text-blue-600', badge: 'bg-blue-500' },
                      { bg: 'from-green-50 to-green-100', border: 'border-green-200', text: 'text-green-700', textDark: 'text-green-900', textLight: 'text-green-600', badge: 'bg-green-500' },
                      { bg: 'from-purple-50 to-purple-100', border: 'border-purple-200', text: 'text-purple-700', textDark: 'text-purple-900', textLight: 'text-purple-600', badge: 'bg-purple-500' }
                    ];
                    const color = colors[index] || colors[0];
                    
                    return (
                      <div key={areaData.area} className={`bg-gradient-to-br ${color.bg} rounded-lg p-4 border ${color.border} hover:shadow-md transition-shadow`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className={`text-xs font-medium ${color.text} px-2 py-1 rounded ${color.badge} text-white`}>
                            #{index + 1}
                          </div>
                        </div>
                        <div className={`text-sm font-semibold ${color.textDark} mb-1 truncate`} title={areaData.area}>{areaData.area}</div>
                        <div className={`text-xl font-bold ${color.textDark}`}>£{Math.round(areaData.sales).toLocaleString('en-GB')}</div>
                        <div className={`text-xs ${color.textLight} mt-1`}>
                          {areaData.customers.toLocaleString('en-GB')} customers
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Report Type Selector and Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
                <select
                  value={filters.reportType}
                  onChange={(e) => setFilters(prev => ({ ...prev, reportType: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white mb-4"
                  disabled={isFetching}
                >
                  <option value="invoice-summary">Invoice Summary</option>
                  <option value="six-month-sales">6M Sales (Area)</option>
                  <option value="area-analysis">Area Analysis</option>
                  <option value="weekly-sales">Weekly Sales</option>
                  <option value="daily-sales">Daily Sales</option>
                  <option value="quotes-report">Quotes Report</option>
                </select>
                <label className="block text-sm font-medium text-gray-700 mb-2">Report Filters</label>
                <div className="flex flex-wrap gap-2">
                  {renderFilters()}
                  
                  {filters.reportType !== 'weekly-sales' && filters.reportType !== 'quotes-report' && (
                    <select
                      value={filters.branch}
                      onChange={(e) => setFilters(prev => ({ ...prev, branch: e.target.value }))}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      disabled={isFetching}
                    >
                      <option value="">All Branches</option>
                      {branches && branches.map((branch) => (
                        <option key={branch._id} value={branch.MamCode}>
                          {branch.MamCode}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              {filters.reportType !== 'quotes-report' && (
                <div>
                  <button
                    onClick={handleFetch}
                    disabled={isFetching}
                    className="px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium shadow-sm transition-colors"
                  >
                    {isFetching && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isFetching ? 'Loading...' : 'Load Report'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {renderReport()}
        </div>
      </div>
    </div>
  );
}

export default SalesAnalysis;

